import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
  Input,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  updateContract as onUpdateContract,
  getContractByIdFull as onGetContractByIdFull,
} from "../../../../slices/thunks";
import { theme } from "../theme";

const EditContractModal = ({ isOpen, toggle, contract }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && contract) {
      setFormData({
        id: contract.id,
        pvc_number: contract.pvc_number,
        pvc_date: contract.pvc_date,
        notary: contract.notary,
        currency: contract.currency,
        type: contract.type,
        notes: contract.notes,
      });
    }
  }, [isOpen, contract]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await dispatch(onUpdateContract(formData)).unwrap();
      // The update endpoint returns the contract's summary shape (e.g. `client`
      // as a bare id), which doesn't match the richer shape selectedContractFull
      // needs (nested client object, units, payments, totals). Refetch it fresh
      // rather than merging two incompatible shapes.
      await dispatch(onGetContractByIdFull(formData.id));
      toggle();
    } catch (error) {
      console.error("Error updating contract:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Edit Contract</ModalHeader>
      <ModalBody>
        <Row className="g-4">
          <Col md={6}>
            <Label for="pvc_number" className="fw-semibold">PVC Number</Label>
            <Input
              bsSize="lg"
              type="text"
              id="pvc_number"
              name="pvc_number"
              value={formData.pvc_number || ""}
              onChange={handleInputChange}
            />
          </Col>
          <Col md={6}>
            <Label for="pvc_date" className="fw-semibold">PVC Date</Label>
            <Flatpickr
              className="form-control form-control-lg"
              id="pvc_date"
              name="pvc_date"
              options={{
                enableTime: false,
                altInput: true,
                altFormat: "d M, Y",
                dateFormat: "Y-m-d",
              }}
              value={formData.pvc_date || ""}
              onChange={(date) => {
                if (date.length > 0) {
                  setFormData((prev) => ({
                    ...prev,
                    pvc_date: moment(date[0]).format("YYYY-MM-DD"),
                  }));
                }
              }}
            />
          </Col>
          <Col md={4}>
            <Label for="notary" className="fw-semibold">Notary</Label>
            <Input
              bsSize="lg"
              type="text"
              id="notary"
              name="notary"
              value={formData.notary || ""}
              onChange={handleInputChange}
            />
          </Col>
          <Col md={4}>
            <Label for="currency" className="fw-semibold">Currency</Label>
            <Input
              bsSize="lg"
              type="select"
              id="currency"
              name="currency"
              value={formData.currency || "RON"}
              onChange={handleInputChange}
            >
              <option value="RON">RON</option>
              <option value="EUR">EUR</option>
            </Input>
          </Col>
          <Col md={4}>
            <Label for="type" className="fw-semibold">Type</Label>
            <Input
              bsSize="lg"
              type="select"
              id="type"
              name="type"
              value={formData.type || ""}
              onChange={handleInputChange}
            >
              <option value="">Select Type</option>
              <option value="PRE-CONTRACT">Pre-Contract</option>
              <option value="CREDIT">Credit</option>
              <option value="CASH">Cash</option>
              <option value="DEVELOPER">Developer</option>
              <option value="OTHER">Other</option>
            </Input>
          </Col>
          <Col md={12}>
            <Label for="notes" className="fw-semibold">Notes</Label>
            <Input
              bsSize="lg"
              type="textarea"
              id="notes"
              name="notes"
              style={{ height: "120px" }}
              value={formData.notes || ""}
              onChange={handleInputChange}
            />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          style={{ backgroundColor: theme.accent, borderColor: theme.accent }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditContractModal;
